import type { AudioFramePayload } from "./types";

export function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

/** 32  bin 峰值归一化频谱 → 列能量（与 EQ 柱模式同一套标尺） */
export function computeUnifiedColumnLevels(
  frame: AudioFramePayload,
  columnCount: number
): number[] {
  const n = frame.spectrum.length;
  if (n <= 0 || columnCount <= 0) return [];

  const beatBoost = frame.beat ? 0.14 : 0;
  const rmsLift = 0.16 + frame.rms * 0.62;
  const levels: number[] = [];

  for (let col = 0; col < columnCount; col += 1) {
    const start = Math.floor((col / columnCount) * n);
    const end = Math.max(start + 1, Math.ceil(((col + 1) / columnCount) * n));
    let peak = 0;
    let sum = 0;
    for (let i = start; i < end; i += 1) {
      const value = frame.spectrum[i] ?? 0;
      sum += value;
      peak = Math.max(peak, value);
    }
    const avg = sum / Math.max(1, end - start);
    const mixed = avg * 0.52 + peak * 0.48;
    levels.push(clamp01(Math.pow(mixed, 0.62) * rmsLift + beatBoost));
  }

  return levels;
}

export type RhythmTransientState = {
  bassEma: number;
  bassDevEma: number;
  lastRms: number;
  lastTriggerAt: number;
};

export function createRhythmTransientState(): RhythmTransientState {
  return { bassEma: 0, bassDevEma: 0, lastRms: 0, lastTriggerAt: 0 };
}

/** 与 beatStrobe / beatSweep 同标尺的瞬态检测（直接用 frame.bass，勿重算 0-255 频谱） */
export function probeRhythmTransient(
  frame: AudioFramePayload,
  state: RhythmTransientState,
  now = frame.timestamp
): { hit: boolean; bassZ: number; ambient: number } {
  const alpha = 0.22;
  state.bassEma += alpha * (frame.bass - state.bassEma);
  const dev = Math.abs(frame.bass - state.bassEma);
  state.bassDevEma += alpha * (dev - state.bassDevEma);
  const bassZ =
    state.bassDevEma > 1e-4
      ? (frame.bass - state.bassEma) / (state.bassDevEma * 1.45)
      : 0;
  const rmsJump = frame.rms - state.lastRms;
  state.lastRms = frame.rms;

  const cooldownMs = 120;
  const canTrigger = now - state.lastTriggerAt >= cooldownMs;
  const bassHit = bassZ > 0.45 && frame.rms > 0.015;
  const rmsSpike = rmsJump > 0.018 && frame.rms > 0.025;
  const strongBass = frame.bass > 0.2 && rmsJump > 0.01;
  const hit =
    canTrigger && (Boolean(frame.beat) || bassHit || rmsSpike || strongBass);
  if (hit) state.lastTriggerAt = now;

  const ambient =
    frame.rms > 0.012
      ? clamp01(0.2 + frame.bass * 0.42 + frame.rms * 0.28 + frame.mids * 0.12)
      : 0;

  return { hit, bassZ, ambient };
}

export function rhythmMusicActive(frame: AudioFramePayload) {
  return frame.rms > 0.015 || frame.bass > 0.06;
}
